import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';

/**
 * Generic inline editable field component
 * 
 * Features:
 * - Supports text, email, tel, url input types
 * - Click to edit inline
 * - Escape to cancel, Enter to confirm
 * - Max length validation
 * - Accessibility support with aria labels
 * 
 * @example
 * <EditableField
 *   value="John Doe"
 *   onChange={(val) => setName(val)}
 *   placeholder="Enter name"
 *   type="text"
 * />
 */
function EditableField({
  value = '',
  onChange,
  placeholder = '',
  type = 'text',
  error,
  disabled = false,
  maxLength,
  className = '',
  onBlur,
  ariaLabel = 'Editable text field',
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef(null);

  // Sync local value with prop changes (only when not editing)
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value);
    }
  }, [value, isEditing]);

  // Auto-focus and select on edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange?.(newValue);
  }, [onChange]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    onBlur?.(localValue);
  }, [localValue, onBlur]);

  const handleClick = useCallback(() => {
    if (!disabled) {
      setIsEditing(true);
    }
  }, [disabled]);

  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'Escape':
        setLocalValue(value);
        setIsEditing(false);
        break;
      case 'Enter':
        handleBlur();
        break;
      default:
        break;
    }
  }, [value, handleBlur]);

  if (!isEditing) {
    return (
      <div
        onClick={handleClick}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        className={`
          p-2 rounded cursor-pointer transition-all
          ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-blue-50 focus:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300'}
          ${error ? 'border-2 border-red-500 bg-red-50' : 'border border-gray-300'}
          ${className}
        `}
      >
        <div className="text-sm text-gray-900 break-words">
          {localValue || <span className="text-gray-400 italic">{placeholder}</span>}
        </div>
        {error && <div className="text-xs text-red-600 mt-1" role="alert">{error}</div>}
      </div>
    );
  }

  return (
    <div>
      <input
        ref={inputRef}
        type={type}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={!!error}
        className={`
          w-full px-2 py-2 text-sm rounded border-2 border-blue-500
          focus:outline-none focus:ring-2 focus:ring-blue-200
          ${error ? 'border-red-500 bg-red-50' : 'bg-white'}
        `}
      />
    </div>
  );
}

EditableField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  type: PropTypes.oneOf(['text', 'email', 'tel', 'url']),
  error: PropTypes.string,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number,
  className: PropTypes.string,
  onBlur: PropTypes.func,
  ariaLabel: PropTypes.string,
};

export default memo(EditableField);
